/*
 * DEV-QUICK-ADD-EXPLICIT-SIZE-001
 *
 * Two narrow corrections to Quick Add. Neither replaces any part of the
 * Quick Add architecture: both drive the theme's OWN product-info /
 * product-form APIs and the theme's OWN card->tray handoff event.
 *
 * 1. COLOUR CONTINUITY ON MOBILE.
 *    Desktop already works and is untouched: GridSwatch.selectSwatch()
 *    (assets/theme.js) rewrites the card's [data-quick-add-inline]
 *    dataset.productUrl to `?variant=<card colour>` and dispatches
 *    `theme:quick-add-inline:variant-changed`, so the inline tray's
 *    `?section_id=api-quick-add-inline` fetch already carries the colour.
 *    The MOBILE drawer is a different component with a different URL
 *    source: QuickAddProduct.renderModal() builds
 *    `products/<handle>?section_id=api-product-upsell&variant=<id>` from
 *    `this.variantId`, which the constructor reads once from the button's
 *    `data-quick-add-modal-variant`. That attribute is only rendered for
 *    forced_variant colourway cards (snippets/quick-add-product.liquid),
 *    so on an ordinary card it is absent and the drawer always fetched the
 *    default colour. Fixed by listening to the SAME stock event the
 *    desktop path already dispatches and writing the card's selected
 *    variant onto that button - attribute and live property both, because
 *    renderModal() reads the property, which was captured at construction.
 *    No second colour-selection system: the card's own swatch state is the
 *    only source, and the handoff stays inside the one card element.
 *
 * 2. SIZE MUST START UNSELECTED.
 *    snippets/api-product-upsell-body.liquid renders the Quick Add payload
 *    with `current_variant = product.selected_or_first_available_variant`,
 *    and snippets/product-variant-options.liquid prints `checked` from
 *    `value.selected`. Shopify marks that variant's option values selected,
 *    so the first available size always arrived checked, the legend read
 *    "Beden XS", [name=id] held a complete variant and Add to Cart was
 *    live before the shopper had chosen anything. Carrying the colour in
 *    (point 1) makes that worse, not better: the card's colour context is
 *    a representative variant such as "Navy / XS", and its size must never
 *    be read as permission to purchase.
 *
 *    So after the fetched markup lands, every NON-colour option group is
 *    returned to an unselected state and the form is made genuinely
 *    incomplete - not merely greyed out:
 *      - no size radio is checked
 *      - the legend's [data-selected-value] is emptied, so it reads "Beden"
 *      - [name=id] is emptied AND disabled, so a form submit from any
 *        source (Enter, double click, screen reader, programmatic) carries
 *        no variant at all
 *      - the submit button gets the theme's own disabled state through
 *        product-form.toggleSubmitButton()
 *    The theme re-renders the picker from the server on every option
 *    change (product-info.handleOptionValueChange), which re-checks a size
 *    again, so this is re-applied on each re-render until the shopper has
 *    explicitly picked a size. Picking one clears the latch, re-enables the
 *    input, and from there the theme's stock variant resolution runs
 *    untouched. Changing colour re-arms the latch, because size
 *    availability differs per colour and silently keeping a size that is
 *    sold out in the new colour is the exact failure this guards against.
 */
(function () {
  'use strict';

  if (window.__definexQuickAddExplicitSize) return;
  window.__definexQuickAddExplicitSize = true;

  /* The two Quick Add surfaces are different elements, not one component:
     the desktop tray is .quick-add-inline__panel inside the card, and the
     mobile drawer is a <dialog class="drawer product-quick-add"> appended to
     the body (QuickAddProduct.modalCreate). An earlier build scoped only to
     the tray and a `quick-add-modal` tag that does not exist in this theme,
     so the mobile drawer kept arriving with XS checked and a live variant id
     - verified against the drawer's real ancestor chain:
     div.product-quick-add__inner < div.drawer__inner.product-quick-add__content
     < dialog.drawer.product-quick-add. */
  var SCOPE = '.quick-add-inline__panel, .product-quick-add';
  var COLOUR_FIELDSET = 'radio__fieldset--swatches';
  var LATCH = 'definexSizeChosen';

  /* Every write below is itself a DOM mutation inside the node the observer
     watches. A boolean "applying" flag does NOT contain that: MutationObserver
     callbacks are delivered asynchronously, so the flag is already back to
     false by the time the queued records are handed over, and the observer
     re-fires forever. Measured the hard way - the first build of this file
     locked the page up so completely that Safari stopped answering
     `do JavaScript` altogether. The observer is therefore disconnected around
     the writes and its queue drained with takeRecords() before reconnecting,
     and every write is made conditional so a settled panel produces no
     mutations at all. */

  function quickAddScope(node) {
    return node && node.closest ? node.closest(SCOPE) : null;
  }

  function optionGroups(root) {
    return [].slice.call(root.querySelectorAll('.radio__fieldset')).filter(function (fs) {
      return !fs.classList.contains(COLOUR_FIELDSET) && fs.querySelector('input[type="radio"]');
    });
  }

  /* The Quick Add payload renders TWO /cart/add forms: the real one inside
     <product-form> and the Shop Pay installment form beside it, each with
     its own [name=id] (product-info.updateVariantInputs writes to both).
     Clearing only the product-form one would leave a complete variant id
     sitting in a second submittable form, so every one in scope is cleared. */
  function variantInputs(info) {
    return [].slice.call(info.querySelectorAll('[name=id]'));
  }

  function disableSubmit(info) {
    var form = info.querySelector('product-form');
    if (form && typeof form.toggleSubmitButton === 'function') {
      var label = info.dataset.sizeRequiredLabel ||
        (window.theme && window.theme.strings && window.theme.strings.addToCart) || null;
      if (label) form.toggleSubmitButton(true, label);
      else form.toggleSubmitButton(true);
      return;
    }
    var btn = info.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('disabled', 'disabled');
  }

  function needsClearing(info) {
    var groups = optionGroups(info);
    if (!groups.length) return false; /* colour-only product: nothing to withhold */

    for (var i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('input[type="radio"]:checked')) return true;
      var label = groups[i].querySelector('[data-selected-value]');
      if (label && label.textContent !== '') return true;
    }
    var inputs = variantInputs(info);
    for (var j = 0; j < inputs.length; j++) {
      if (inputs[j].value !== '' || !inputs[j].disabled) return true;
    }
    var btn = info.querySelector('[type="submit"]');
    if (btn && !btn.hasAttribute('disabled')) return true;
    return false;
  }

  function clearSizeSelection(info) {
    if (!needsClearing(info)) return;

    optionGroups(info).forEach(function (fs) {
      [].slice.call(fs.querySelectorAll('input[type="radio"]')).forEach(function (input) {
        if (input.checked) input.checked = false;
      });
      var selectedLabel = fs.querySelector('[data-selected-value]');
      if (selectedLabel && selectedLabel.textContent !== '') selectedLabel.textContent = '';
    });

    variantInputs(info).forEach(function (input) {
      input.value = '';
      /* disabled inputs are not submitted at all - value alone would still
         be a submittable field if anything repopulated it */
      input.disabled = true;
    });

    disableSubmit(info);

    /* product-info snapshots the confirmed selection in connectedCallback,
       before this runs; without re-snapshotting, a failed option fetch
       would revertToConfirmedSelection() back to the pre-cleared state. */
    if (typeof info.snapshotConfirmedSelection === 'function') {
      try { info.snapshotConfirmedSelection(); } catch (e) {}
    }
  }

  /* DEV-QUICK-ADD-SINGLE-MEDIA-UX-002 — keep the one visible Quick Add image
     in step with the chosen colour.

     api-product-upsell-body.liquid renders one [data-quick-add-media-frame]
     per colour, stacked, and collections.css shows only `.is-visible`. The
     theme's own product-info.updateMedia() cannot drive this: it looks up
     `[data-image-id="<media id>"]` and scrolls the old `.product__slides`
     rail, which no longer exists here, so it finds nothing and returns. That
     rail was also the bug - on parasut-kumas-yagmurluk choosing Siyah left
     scrollLeft at 0 and kept the Gri photo on screen.

     Matching is by colour name only, never by full variant title, so a colour
     still resolves when the shopper's size differs from whichever size the
     frame was rendered from. Nothing is fetched: every frame is already in the
     payload. */
  function showColourMedia(info, colourValue) {
    if (!colourValue) return;
    var frames = info.querySelectorAll('[data-quick-add-media-frame]');
    if (!frames.length) return;

    var matched = null;
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].getAttribute('data-colour') === colourValue) {
        matched = frames[i];
        break;
      }
    }
    /* No frame for this colour (a product with no colour option at all, say):
       leave whatever is showing rather than blanking the panel. */
    if (!matched) return;
    var selectedImage = matched.querySelector('img');
    if (selectedImage) {
      selectedImage.loading = 'eager';
      selectedImage.fetchPriority = 'high';
    }

    for (var j = 0; j < frames.length; j++) {
      frames[j].classList.toggle('is-visible', frames[j] === matched);
    }
  }

  function armedFor(info) {
    return info.dataset[LATCH] !== 'true';
  }

  function watch(info) {
    if (info.__definexSizeWatch) return;
    info.__definexSizeWatch = true;

  /* `disabled` is watched as well as childList because ProductForm's own
     constructor runs `this.variantIdInput.disabled = false` when the fetched
     markup upgrades, which can land AFTER product-info has already dispatched
     theme:product-info:loaded and this file has cleared the form. Measured in
     the mobile drawer: the purchase form came back as value="" but
     disabled=false, so FormData still carried an empty `id` field. A childList
     observer never sees that - it is an attribute change. */
    var options = {childList: true, subtree: true, attributes: true, attributeFilter: ['disabled']};
    var observer = new MutationObserver(function () {
      if (!armedFor(info)) return;
      if (!needsClearing(info)) return;
      apply();
    });

    function apply() {
      observer.disconnect();
      try {
        clearSizeSelection(info);
      } finally {
        observer.takeRecords();
        observer.observe(info, options);
      }
    }

    apply();
  }

  /* product-info dispatches this from its own connectedCallback, bubbling,
     for the desktop tray and the mobile drawer alike. */
  document.addEventListener('theme:product-info:loaded', function (event) {
    var info = event.target;
    if (!info || !quickAddScope(info)) return;
    watch(info);
  });

  // A failed first size request has no confirmed size to restore. Re-arm the
  // explicit-choice guard so the same size can be clicked again safely.
  document.addEventListener('theme:product-info:selection-restored', function (event) {
    var info = event.target;
    if (!info || !quickAddScope(info)) return;
    var incomplete = optionGroups(info).some(function (fs) {
      return !fs.querySelector('input[type="radio"]:checked');
    });
    if (incomplete) {
      info.dataset[LATCH] = 'false';
      clearSizeSelection(info);
      disableSubmit(info);
    }
    var colour = info.querySelector('.' + COLOUR_FIELDSET + ' input:checked');
    if (colour) showColourMedia(info, colour.value);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || input.type !== 'radio') return;
    var scope = quickAddScope(input);
    if (!scope) return;
    var info = input.closest('product-info');
    if (!info) return;

    var fieldset = input.closest('.radio__fieldset');
    if (!fieldset) return;

    if (fieldset.classList.contains(COLOUR_FIELDSET)) {
      /* New colour, new availability map: require the size again. */
      info.dataset[LATCH] = 'false';
      showColourMedia(info, input.value);
      return;
    }

    /* Explicit size choice: release the latch and hand the form back to the
       theme, which sets [name=id] itself on the next render. */
    info.dataset[LATCH] = 'true';
    variantInputs(info).forEach(function (variant) {
      variant.disabled = false;
    });
  });

  /* Mobile colour continuity - see point 1 above. */
  document.addEventListener('theme:quick-add-inline:variant-changed', function (event) {
    var details = event.detail && event.detail.details;
    if (!details) return;

    var card = details.closest('.product-item');
    if (!card) return;

    var match = /[?&]variant=(\d+)/.exec(details.dataset.productUrl || '');
    if (!match) return;

    var component = card.querySelector('quick-add-product');
    if (!component) return;

    var button = component.querySelector('[data-quick-add-modal-handle]');
    if (button) button.setAttribute('data-quick-add-modal-variant', match[1]);
    /* renderModal() reads the property, captured in the constructor. */
    component.variantId = match[1];
  });
})();
